import { useState, useCallback, useEffect } from 'react';
import type { Word, ReframesTier } from '../../types';
import { useUiStore } from '../../stores/uiStore';
import CardGrid from '../CardGrid';
import ApproveButton from '../ApproveButton';
import CompactToggle from '../CompactToggle';
import { resolveSelectedText } from '../../utils/resolveSelected';
import './StageCommon.css';

/**
 * Stage 6: Reframes — 3-tier pick (Newsletter, Second Chance, Third Chance).
 * Each tier: 3 cards, pick 1.
 */

interface ReframesStageProps {
  word: Word;
  config: { tiers: ReframesTier[] };
  onGenerate: () => void;
  onApprove: (selections: Record<string, unknown>) => void;
  onEdit?: (fieldId: string, newContent: string) => void;
  onRegenerateField?: (fieldName: string, instructions?: string) => Promise<{ value: string; model: string | null } | null>;
  generating: boolean;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
}

export default function ReframesStage({ word, config, onGenerate, onApprove, onEdit, onRegenerateField, generating, saving, registerApprovePayload }: ReframesStageProps) {
  const tiers = config.tiers;
  const hasContent = tiers[0].cardFields.some((f) => word[f]);
  const compactCards = useUiStore((s) => s.compactCards);
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null);

  // Per-field regen (parity with PickStage's handleRegenerateInline, 2026-07-05).
  // The server keeps this call BLIND — /api/generate/stage6 with regenerateField
  // routes through the same isolated buildReframesPrompt as the mass regen (no
  // word name, no fakes), narrowed to the one requested reframe.
  // One-level regen undo (session-only, per word) — mirrors PickStage.
  const [undoValues, setUndoValues] = useState<Record<string, string>>({});
  useEffect(() => { setUndoValues({}); }, [word.id]);

  const handleRegenerateInline = useCallback(async (fieldId: string, instructions?: string) => {
    if (!onRegenerateField || regeneratingField) return;
    const prev = String(word[fieldId] ?? '');
    setRegeneratingField(fieldId);
    try {
      const res = await onRegenerateField(fieldId, instructions);
      if (res && onEdit) {
        if (prev.trim()) setUndoValues((u) => ({ ...u, [fieldId]: prev }));
        onEdit(fieldId, res.value);
      }
    } finally {
      setRegeneratingField(null);
    }
  }, [onRegenerateField, regeneratingField, onEdit, word]);

  const handleUndoRegen = useCallback((fieldId: string) => {
    const prev = undoValues[fieldId];
    if (prev === undefined || !onEdit) return;
    onEdit(fieldId, prev);
    setUndoValues((u) => {
      const next = { ...u };
      delete next[fieldId];
      return next;
    });
  }, [undoValues, onEdit]);

  const [tierSelections, setTierSelections] = useState<Record<string, Set<string>>>(() => {
    const initial: Record<string, Set<string>> = {};
    tiers.forEach((tier) => {
      const stored = word[tier.selectedField];
      if (stored !== undefined && stored !== null && stored !== '') {
        const storedStr = String(stored);
        tier.cardFields.forEach((cf) => {
          const m = cf.match(/_(\d+)$/);
          if (m && m[1] === storedStr) {
            initial[tier.selectedField] = new Set([cf]);
          }
        });
      }
      if (!initial[tier.selectedField]) {
        initial[tier.selectedField] = new Set();
      }
    });
    return initial;
  });

  const allTiersMet = tiers.every((tier) =>
    (tierSelections[tier.selectedField]?.size || 0) >= tier.requiredSelections
  );

  const handleTierSelect = useCallback((tierField: string, cardId: string) => {
    setTierSelections((prev) => ({
      ...prev,
      [tierField]: new Set([cardId]),
    }));
  }, []);

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => {
      if (!allTiersMet) return null;
      const selections: Record<string, unknown> = {};
      tiers.forEach((tier) => {
        const selectedCardId = tierSelections[tier.selectedField]?.values().next().value as string | undefined;
        if (selectedCardId) {
          const match = selectedCardId.match(/_(\d+)$/);
          if (match) selections[tier.selectedField] = match[1];
        }
      });
      return selections;
    });
  }, [registerApprovePayload, allTiersMet, tiers, tierSelections]);

  const handleApprove = useCallback(() => {
    const selections: Record<string, unknown> = {};
    tiers.forEach((tier) => {
      const selectedCardId = tierSelections[tier.selectedField]?.values().next().value as string | undefined;
      if (selectedCardId) {
        const match = selectedCardId.match(/_(\d+)$/);
        if (match) selections[tier.selectedField] = match[1];
      }
    });
    onApprove(selections);
  }, [tiers, tierSelections, onApprove]);

  return (
    <div className="stage-view">
      {!hasContent && (
        <div className="stage-generate-area">
          <button className="generate-btn" onClick={onGenerate} disabled={generating} title="Generate Reframes (\u2318G)">
            {generating ? 'Generating...' : 'Generate Reframes'}
          </button>
        </div>
      )}

      {hasContent && (
        <div className="stage-toolbar">
          <button className="regenerate-btn" onClick={onGenerate} disabled={generating} title="Regenerate (\u2318R)">
            {generating ? 'Regenerating...' : 'Regenerate'}
          </button>
          <CompactToggle />
        </div>
      )}

      {hasContent && (
        <div className="reframes-tiers">
          {tiers.map((tier) => {
            const cards = tier.cardFields
              .filter((f) => word[f])
              .map((f) => ({
                id: f,
                content: String(word[f] || ''),
                label: formatFieldLabel(f),
              }));

            const contextFields = (tier.contextFields || [])
              .filter((f) => word[f])
              .map((f) => ({
                label: formatFieldLabel(f),
                value: resolveSelectedText(f, word) ?? String(word[f] || ''),
              }));

            return (
              <div key={tier.selectedField} className="reframes-tier">
                <h3 className="reframes-tier-title">{tier.title}</h3>
                <CardGrid
                  cards={cards}
                  selectedIds={tierSelections[tier.selectedField] || new Set()}
                  onSelect={(id) => handleTierSelect(tier.selectedField, id)}
                  onEdit={onEdit}
                  onRegenerate={onRegenerateField ? handleRegenerateInline : undefined}
                  regeneratingId={regeneratingField}
                  undoableIds={new Set(Object.keys(undoValues))}
                  onUndo={handleUndoRegen}
                  showCheckboxes={true}
                  requiredSelections={tier.requiredSelections}
                  contextFields={contextFields}
                  compact={compactCards}
                />
              </div>
            );
          })}

          <ApproveButton
            onClick={handleApprove}
            disabled={!allTiersMet || saving}
            loading={saving}
            label="Approve All Reframes"
          />
        </div>
      )}

      {generating && !hasContent && (
        <div className="stage-generating">
          <div className="generating-spinner" />
          <p>Generating reframes...</p>
        </div>
      )}
    </div>
  );
}

function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
